import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import { ListSection, Section, StructuredListSection } from 'common';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import strings from 'strings';
import { FPCard, FPCardContent, FPCardHeader } from 'UI/components/atoms/FPCard';
import FPLink from 'UI/components/atoms/FPLink';
import InfoLabel from 'UI/components/molecules/InfoLabel';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { Endpoints } from 'UI/constants/endpoints';
import { dateFormatter, normalizeUrl } from 'UI/utils';
import { filterValidItems, isTruthyValue } from 'utils';

import {
  Certifications,
  Education,
  Experience,
  Languages,
  OrganizationalExperienceSection,
  References,
  Summary,
  Trainings
} from '../components';
import { PARSED_STATUSES } from '../constants';
import { formatMonthYear } from '../utils';

import { LoaderContainer, SelectFileLabel } from './ResumeViewer.styled';

const COPIES = strings.candidates.profile.resumeTab;

const renderEmails = (emails = []) =>
  Array.isArray(emails) && emails?.length > 0
    ? emails
        ?.filter(email => isTruthyValue(email))
        .map((email, i) => (
          <React.Fragment key={email}>
            {i > 0 && <span>&nbsp;&#183;&nbsp;</span>}
            <FPLink
              style={{ display: 'inline-block' }}
              href={`mailto:${email}`}
              linkProps={{ type: 'external', url: email }}
            />
          </React.Fragment>
        ))
    : null;

const renderPhones = (phones = []) =>
  Array.isArray(phones) && phones?.length > 0
    ? phones
        ?.filter(phone => isTruthyValue(phone))
        .map((phone, i) => (
          <React.Fragment key={phone}>
            {i > 0 && <span>&nbsp;&#183;&nbsp;</span>}
            <FPLink
              linkProps={{
                type: 'ringCentral',
                url: phone,
                customLinkProps: { formatPhone: true }
              }}
            />
          </React.Fragment>
        ))
    : null;

const renderLinks = (urls = []) =>
  Array.isArray(urls) && urls?.length > 0
    ? urls
        ?.filter(url => isTruthyValue(url))
        .map(linkUrl => {
          const normalized = normalizeUrl(linkUrl);
          return (
            <FPLink
              key={normalized}
              linkProps={{ target: '_blank', type: 'external', url: normalized }}
            />
          );
        })
    : null;

export const ResumeViewer = ({ candidateId }) => {
  const { Status, state } = useFetchWithStatusV2({
    endpoint: Endpoints.CandidateParsedResumes.replace(':id', candidateId)
  });

  const results = state?.results;
  const [selectedFile, setSelectedFile] = useState(null);
  const selectedResume = selectedFile || results?.[0];

  const profile = selectedResume?.result?.data?.profile;

  const {
    achievements_and_accomplishments: achievements,
    assessments,
    awards_honors_recognitions: awards,
    basics,
    certifications_and_licenses: certifications,
    educations,
    hobbies_interests: hobbies,
    languages,
    military_experience: militaryExperience,
    professional_associations_and_memberships: professionalAssociations,
    professional_experiences: professionalExperience,
    projects,
    references,
    research_and_publications: researchAndPublications,
    skills,
    trainings_courses_and_workshops: trainingsAndCourses,
    volunteer_experiences: volunteerExperiences
  } = profile || {};
  const {
    address,
    date_of_birth: birthDate,
    total_experience_in_years: experienceYears,
    has_driving_license: hasDrivingLicense,
    emails,
    first_name: firstName,
    last_name: lastName,
    phone_numbers: phoneNumbers,
    profession,
    summary,
    urls
  } = basics || {};
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  const { year, month, day } = birthDate || {};
  const formattedBirthDate =
    birthDate &&
    dateFormatter(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  const emailsToRender = renderEmails(emails);
  const phoneNumbersToRender = renderPhones(phoneNumbers);
  const linksToRender = renderLinks(urls);
  const cleanAssessments = filterValidItems(assessments);
  const formattedAssessments =
    cleanAssessments &&
    cleanAssessments.map(assessment => {
      const { title, description, result, date } = assessment;

      const formattedDate = date && formatMonthYear(date);
      const details = [result, description, formattedDate].filter(Boolean).join(' • ');

      return {
        title,
        description: details
      };
    });

  const handleChange = event => {
    setSelectedFile(event.target.value);
  };

  const renderResumeContent = () => {
    if (selectedResume?.status === PARSED_STATUSES.error) {
      return (
        <Typography variant="body1" color="textSecondary">
          {selectedResume?.result?.error || COPIES.parseError}
        </Typography>
      );
    }

    return (
      <>
        <Section>
          <Grid container spacing={4}>
            <Grid container item>
              {emailsToRender && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={emailsToRender}
                    title="Email"
                    variant="body1"
                  />
                </Grid>
              )}
              {phoneNumbersToRender && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={phoneNumbersToRender}
                    title="Phone"
                    variant="body1"
                  />
                </Grid>
              )}
              {formattedBirthDate && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={formattedBirthDate}
                    title="Birth Date"
                    variant="body1"
                  />
                </Grid>
              )}
              {experienceYears && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={experienceYears}
                    title="Experience Years"
                    variant="body1"
                  />
                </Grid>
              )}
              {address && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={address}
                    title="Address"
                    variant="body1"
                  />
                </Grid>
              )}
              {hasDrivingLicense && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={hasDrivingLicense ? strings.shared.ui.yes : strings.shared.ui.no}
                    title="Has Driving License"
                    variant="body1"
                  />
                </Grid>
              )}
              {linksToRender && (
                <Grid item xs={12} sm={6} md={4}>
                  <InfoLabel
                    cropped={false}
                    description={linksToRender}
                    title="Links"
                    variant="body1"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Section>

        <Summary summary={summary} />
        <ListSection title={COPIES.sections.skills} items={skills} />
        <Experience experiences={professionalExperience} />
        <Education educations={educations} />
        <Languages languages={languages} />
        <Certifications certifications={certifications} />
        <Trainings trainings={trainingsAndCourses} />
        <StructuredListSection title={COPIES.sections.achievements} items={achievements} />
        <StructuredListSection
          title={COPIES.sections.researchAndPublications}
          items={researchAndPublications}
        />
        <StructuredListSection title={COPIES.sections.awards} items={awards} />
        <StructuredListSection title={COPIES.sections.projects} items={projects} />
        <OrganizationalExperienceSection
          title={COPIES.sections.professionalAssociations}
          items={professionalAssociations}
        />
        <OrganizationalExperienceSection
          title={COPIES.sections.volunteerExperiences}
          items={volunteerExperiences}
        />
        <StructuredListSection title={COPIES.sections.assessments} items={formattedAssessments} />
        <Experience experiences={militaryExperience} title={COPIES.sections.militaryExperience} />
        <ListSection title={COPIES.sections.hobbies} items={hobbies} />
        <References references={references} />
      </>
    );
  };

  return (
    <Status
      loading={
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      }
      success={() => (
        <>
          {selectedResume && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2}>
              <SelectFileLabel color="textSecondary" paragraph={false} variant="body2">
                {COPIES.selectFile}
              </SelectFileLabel>

              <Select value={selectedResume} onChange={handleChange}>
                {results?.map(resume => (
                  <MenuItem key={resume?.fileName} value={resume}>
                    <Typography variant="body2">{resume?.fileName}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <FPCard>
            <FPCardHeader
              title={isTruthyValue(fullName) ? fullName : ''}
              subheader={isTruthyValue(profession) ? profession : ''}
            />
            <FPCardContent>{renderResumeContent()}</FPCardContent>
          </FPCard>

          <Typography variant="body2" color="textSecondary" align="right" style={{ marginTop: 16 }}>
            {selectedResume?.parsingProvider ||
              selectedResume?.result?.response?.model ||
              COPIES.emptyModel}
          </Typography>
        </>
      )}
      error={error => (
        <Typography paragraph gutterBottom>
          {error}
        </Typography>
      )}
      empty={<EmptyPlaceholder subtitle={COPIES.emptyState.subtitle} />}
    />
  );
};
